/**
 * Motor de descarga de carpeta como ZIP — lo consume la vista clásica
 * (documents/download_folder.js).
 * Ver spec docs/superpowers/specs/2026-07-15-descarga-carpeta-selectiva-design.md
 *
 * Dos fases: cada vista arma su lista de trabajo (censo: documentos +
 * adjuntos, con resolveAttachments) y downloadZipAndSave la descarga con
 * progreso global único (el total se fija ANTES de descargar, así la barra
 * sube de 0 a 100 una sola vez).
 */

import { toAccountingList } from './accounting_refs';

const stripExt = (name) => {
  const s = String(name || '');
  const i = s.lastIndexOf('.');
  return i > 0 ? s.slice(0, i) : s;
};

const extOf = (name) => {
  const s = String(name || '');
  const i = s.lastIndexOf('.');
  return i > 0 ? s.slice(i) : '';
};

/**
 * Nombre del adjunto dentro del ZIP, prefijado con su documento.
 * `index` numera contabilizaciones múltiples a partir de la segunda
 * (" 2", " 3", …); 1/omitido conserva el nombre clásico.
 */
export const attachmentZipName = (docName, kind, attachName, index) => {
  const base = stripExt(docName);
  if (kind === 'xml') return `${base} — XML${extOf(attachName) || '.xml'}`;
  if (kind === 'accounting') {
    const suffix = index > 1 ? ` ${index}` : '';
    return `${base} — Contabilizacion${suffix}${extOf(attachName)}`;
  }
  return `${base} — Vinculado — ${stripExt(attachName)}${extOf(attachName)}`;
};

/**
 * Resuelve los adjuntos de un doc de files/ según las opciones marcadas.
 * Tolerante: refs rotas o docs inexistentes se omiten sin error.
 */
export const resolveAttachments = async (docData, options = {}) => {
  const out = [];
  const safeGet = async (ref) => {
    try {
      if (!ref || typeof ref.get !== 'function') return null;
      const snap = await ref.get();
      return snap?.exists ? snap.data() : null;
    } catch { return null; }
  };

  if (options.xml) {
    const d = await safeGet(docData?.xml);
    if (d?.url) out.push({ name: attachmentZipName(docData.name, 'xml', d.name), url: d.url });
  }
  if (options.accounting) {
    // El campo admite varias (array) o una suelta (legacy): se incluyen
    // todas, numeradas desde la segunda.
    let n = 0;
    for (const ref of toAccountingList(docData?.accounting)) {
      const d = await safeGet(ref);
      if (d?.url) {
        n += 1;
        out.push({ name: attachmentZipName(docData.name, 'accounting', d.name, n), url: d.url });
      }
    }
  }
  if (options.links && Array.isArray(docData?.links)) {
    for (const link of docData.links) {
      const d = await safeGet(link);
      if (d?.url) out.push({ name: attachmentZipName(docData.name, 'link', d.name), url: d.url });
    }
  }
  return out;
};

/**
 * Descarga la lista de trabajo, arma el ZIP y dispara la descarga del
 * navegador. onProgress se emite ANTES de cada fetch con el contexto del
 * item en curso. Un item que falla se acumula en skipped y no aborta.
 */
/**
 * Carpetas del recorrido que no tienen NINGÚN archivo debajo (ni directo ni en
 * subcarpetas). Las que sí tienen ya aparecen en el ZIP por la ruta de sus
 * archivos; estas hay que agregarlas como carpeta.
 */
export const emptyFolderPaths = (folders, items) => {
  const withFiles = (items || []).map((it) => it && it.zipPath).filter(Boolean);
  const out = [];
  new Set((folders || []).filter(Boolean)).forEach((p) => {
    if (!withFiles.some((z) => z === p || z.startsWith(`${p}/`))) out.push(p);
  });
  return out;
};

/**
 * `emptyFolders`: rutas de carpetas vacías que se agregan al ZIP (opción
 * «Incluir las carpetas vacías»). Con ellas se puede descargar aunque no haya
 * ningún archivo: el ZIP trae solo la estructura.
 */
export const downloadZipAndSave = async ({ items, zipName, onProgress, emptyFolders = [] }) => {
  if ((!items || items.length === 0) && emptyFolders.length === 0) {
    throw new Error('No hay archivos para descargar con las opciones seleccionadas');
  }
  items = items || [];

  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  const total = items.length;
  const skipped = [];
  let current = 0;

  for (const item of items) {
    current++;
    if (onProgress) onProgress({
      current, total,
      percent: Math.round((current / total) * 100),
      folderLabel: item.folderLabel || '',
      fileName: item.name
    });
    try {
      const response = await fetch(item.url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      zip.file(item.zipPath ? `${item.zipPath}/${item.name}` : item.name, blob);
    } catch (err) {
      console.warn('[folder_zip] omitido:', item.name, err?.message);
      skipped.push(item.name);
    }
  }

  if (total > 0 && skipped.length === total) {
    throw new Error('No se pudo descargar ningún archivo de la carpeta');
  }
  emptyFolders.forEach((p) => zip.folder(p));

  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  });
  const url = URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${zipName || 'carpeta'}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return { total, skipped };
};
